import { linkTargetOf, t, type LinkTarget } from '@bms2/shared';
import type { RowActions, TileAnswer, TileRow } from './tileItems';

/**
 * The row-action registry (decision 0309): one entry per tile whose rows offer
 * a pencil, an End or a Remove, keyed on the tile's register key. A record
 * page asks here and never branches on a tile key itself, so a row's control
 * comes from one place on every record page and a tile added later joins by
 * one row in this map.
 */
export interface RowActionContext {
  /** The viewer's own person, for the note only its author edits (decision 0301). */
  personId: string;
  /** Row ends that need no dialog: the API is asked and the page reloads. */
  endRow: (tile: string, row: TileRow) => void;
  checkOut: (checkInId: string) => void;
  release: (row: TileRow) => void;
  editAttribute: (row: TileRow) => void;
  editSupplier: (row: TileRow) => void;
  editNote: (row: TileRow) => void;
  endRequirement: (row: TileRow) => void;
  /** The contact row's pencil: a correction in place (product owner, 2026-09-12). */
  editContactMethod: (row: TileRow) => void;
  /** The employment row's pencil: the title held today, the job's work email and work phone, and its dates (decision 0589). */
  editEmployment: (row: TileRow) => void;
  /** 🔒 The pencil on an open position at our own Company: Set level, the dated position a move writes (T726; decision 1495). */
  setLevel: (row: TileRow) => void;
  /** The site row's pencil on the Company's Sites tile and the Location's Occupants tile: the occupancy and its dates (decision 0589). */
  editOccupancy: (row: TileRow) => void;
  /** The team row's pencil: the role and the share change in place (product owner, 2026-09-12). */
  editTeamMember: (row: TileRow) => void;
  /** Share on a file row: the one Share modal on that file (decision 0345). */
  shareFile: (row: TileRow) => void;
  /** A live certificate row's pencil: the certificate modal keyed to the row (decision 0676). */
  editCertificate: (row: TileRow) => void;
  /** A live certificate row's End: a dialog that says what ending does (decision 0676). */
  endCertificate: (row: TileRow) => void;
  /** 🔒 Remove and Move on a certificate no document names, each a dialog (decision 7891). Absent, no row offers them. */
  removeCertificate?: (row: TileRow) => void;
  moveCertificate?: (row: TileRow) => void;
  /** 🔒 The client's terms set row's pencil on the Access & agreements tile (decision 1172). */
  editTermsSet: (row: TileRow) => void;
  /** 🔒 A company mailbox row's pencil: its window, which corrects and ends the row (decision 2972). */
  editMailbox?: (row: TileRow) => void;
  /** 🔒 + Add Owner on the last Owned By line of General Information (decision 7501). Absent, no row offers it. */
  addOwner?: () => void;
  /** 🔒 End Ownership, the pencil on an Owned By line and an open Subsidiaries row (decision 7501). Absent, no row offers it. */
  endOwnership?: (row: TileRow) => void;
  /** 🔒 Delete this link: the reason window over the link the row names (T728; decision 1500). Absent, no row offers it. */
  deleteLink?: (link: LinkTarget) => void;
  /** 🔒 Fold into on a site row the server offers it on: the window naming both rows (decision 1920). Absent, no row offers it. */
  foldSite?: (row: TileRow) => void;
}

type Entry = (tile: TileAnswer, ctx: RowActionContext) => RowActions;

const live = (row: TileRow) => row.pill !== 'Ended' && row.raw?.ended !== true;

/** An open ownership the server says this viewer may end (decision 7501). */
const ownershipEndable = (row: TileRow) => typeof row.raw?.ownershipId === 'string' && row.raw.editable === true;

/**
 * The tiles whose rows are ended from the row itself, each with the API call the
 * page makes for it. 🔨 Five tiles left this list on 2026-09-20 — an employment,
 * a Company contact, a site, an occupant and a contact method — because each of
 * them records a date or deactivates something, so its end moved inside the row's
 * own edit modal, which asks for the day and confirms (decision 0589). Their end
 * routes did not change; the modal calls them, so the page no longer needs a
 * branch here for any of the five.
 */
export const ENDABLE_TILES = ['dealTeam', 'dealContacts', 'accountRoles', 'roles', 'accessAndAgreements'] as const;
export type EndableTile = (typeof ENDABLE_TILES)[number];

/** Share on every Files tile (decision 0345): one entry per file tile the register names, and the Deal's after Release on the one File row (decision 6040). */
const shareRows: Entry = (_tile, ctx) => ({ onFileShare: (row) => ctx.shareFile(row) });

/**
 * 🔒 Release on a file row (piece 9; decision 6040), Share as its second button: the Deal's, the Company's and the
 * Location's Files tiles, the kinds the server's release rules name and release to a company (decision 11480). A
 * Person's files carry Share alone, because the server releases a Person's file to no company.
 */
const releaseRows: Entry = (tile, ctx) => ({ onRowAction: (id) => { const row = tile.rows?.find((r) => r.id === id); if (row) ctx.release(row); }, rowActionLabel: t('files.release.button'), rowActionGlyph: 'forward', onFileShare: (row) => ctx.shareFile(row) });

/** An End on a live row, through the page's end route for the tile; the tile key names the route. */
const endLive = (key: EndableTile, label = t('record.tile.end')): Entry => (tile, ctx) => ({
  onRowAction: (id) => { const row = tile.rows?.find((r) => r.id === id); if (row) ctx.endRow(key, row); },
  rowActionLabel: label,
  rowActionWhen: live,
});

const REGISTRY: Readonly<Record<string, Entry>> = {
  // 🔨 The row carries the pencil and no End (decision 0589): ending an employment
  // deactivates its login (decision 0165) and takes a date, so it happens inside the
  // modal where the date is typed and the consequence is read, never on one press of a
  // row. The API decides which rows offer the pencil, because our own Company's
  // employments are hired and edited through HR (decision 0165).
  // 🔒 An open position at our own Company carries Set level in the same pencil's place (T726; decision 1495); the API decides
  // which rows take it, the holder of the org chart's permission alone.
  employments: (_tile, ctx) => ({
    onRowEdit: (row) => (row.raw?.levelEditable === true ? ctx.setLevel(row) : ctx.editEmployment(row)),
    rowEditLabel: t('record.tile.editEmployment'),
    rowEditLabelFor: (row) => (row.raw?.levelEditable === true ? t('employment.level.set') : t('record.tile.editEmployment')),
    rowEditWhen: (row) => row.raw?.editable === true || row.raw?.levelEditable === true,
  }),
  // The Company's Contacts are the same employments read from the other end, so they carry the same pencil and the same modal (decision 0589).
  contacts: (_tile, ctx) => ({
    onRowEdit: (row) => ctx.editEmployment(row),
    rowEditLabel: t('record.tile.editEmployment'),
    rowEditWhen: (row) => row.raw?.editable === true,
  }),
  // A site's occupancy is corrected and ended from one modal, opened from the Company's Sites tile and the Location's Occupants tile; the row stays visible as history (decisions 0167, 0589).
  // 🔒 Decision 1920: a row the server offers Fold into carries it, naming the row it folds into.
  sites: (tile, ctx) => ({
    onRowEdit: (row) => ctx.editOccupancy(row),
    rowEditLabel: t('occupancyModal.edit'),
    rowEditWhen: (row) => row.raw?.editable === true,
    ...(ctx.foldSite ? {
      onRowAction: (id: string) => { const row = tile.rows?.find((r) => r.id === id); if (row) ctx.foldSite?.(row); },
      rowActionLabel: t('sites.fold.into'),
      rowActionLabelFor: (row: TileRow) => `${t('sites.fold.into')} ${typeof row.raw?.foldIntoLabel === 'string' ? row.raw.foldIntoLabel : ''}`.trim(),
      rowActionWhen: (row: TileRow) => typeof row.raw?.foldIntoId === 'string',
    } : {}),
  }),
  occupants: (_tile, ctx) => ({
    onRowEdit: (row) => ctx.editOccupancy(row),
    rowEditLabel: t('occupancyModal.edit'),
    rowEditWhen: (row) => row.raw?.editable === true,
  }),
  // An Active agreement is revoked from its timeline entry; a Draft, Expired or Revoked one offers nothing (piece 9).
  // 🔒 The client's terms set row carries a pencil for an administrator and no Revoke (decision 1172).
  accessAndAgreements: (tile, ctx) => ({
    ...endLive('accessAndAgreements', t('nda.review.revoke'))(tile, ctx),
    rowActionWhen: (row) => live(row) && row.raw?.termsSet !== true,
    onRowEdit: (row) => ctx.editTermsSet(row),
    rowEditLabel: t('company.termsSet.edit'),
    rowEditWhen: (row) => row.raw?.termsSet === true && row.raw?.editable === true,
  }),
  checkIns: (_tile, ctx) => ({ onRowAction: (id) => ctx.checkOut(id), rowActionLabel: t('record.tile.checkOut'), rowActionWhen: (row) => row.pill === 'Open' }),
  dealFiles: releaseRows,
  companyFiles: releaseRows,
  personFiles: shareRows,
  siteFiles: shareRows,
  locationFiles: releaseRows,
  itemFiles: shareRows,
  leadFiles: shareRows,
  attributes: (_tile, ctx) => ({ onRowEdit: (row) => ctx.editAttribute(row), rowEditLabel: t('item.attribute.edit') }),
  // 🔨 Every supplier row carries the pencil, an ended one included, because a status set there reopens it; the row carries no End (decision 0337).
  sourcing: (_tile, ctx) => ({
    onRowEdit: (row) => ctx.editSupplier(row),
    rowEditLabel: t('item.supplier.edit'),
  }),
  // 🔨 The pencil is the author's alone (decision 0301); the server refuses anybody else's edit as well.
  // 🔨 A note written in File details reads here and is not edited here (decision 0605): File
  // details is the one surface that writes it, so the file's note carries no pencil.
  internalNotes: (_tile, ctx) => ({ onRowEdit: (row) => ctx.editNote(row), rowEditLabel: t('note.edit'), rowEditWhen: (row) => Boolean(ctx.personId) && row.raw?.authorPersonId === ctx.personId && row.raw?.onFile !== true }),
  // 🔒 A live certificate row carries Edit and End; an ended one is history and carries neither (decision 0676).
  // 🔒 Decision 7891: a certificate no document names carries Remove, and a live one Move; one a document names carries End alone.
  certificates: (tile, ctx) => ({
    onRowEdit: (row) => ctx.editCertificate(row),
    rowEditLabel: t('certificate.edit'),
    rowEditWhen: live,
    onRowAction: (id) => { const row = tile.rows?.find((r) => r.id === id); if (row) ctx.endCertificate(row); },
    rowActionLabel: t('certificate.end'),
    rowActionWhen: live,
    rowMenuActs: (row) => certificateMenuActs(row, ctx),
  }),
  // The site's own open rows end from the tile, a waiver among them; a Company default is not the site's to end (decision 0336).
  // 🔒 Decision 10501: a row the server marks removable carries Remove where End stands, through the same window.
  requirements: (tile, ctx) => ({
    onRowAction: (id) => { const row = tile.rows?.find((r) => r.id === id); if (row) ctx.endRequirement(row); },
    rowActionLabel: t('requirement.end'),
    rowActionLabelFor: (row) => (row.raw?.removable === true ? t('requirement.remove') : t('requirement.end')),
    rowActionWhen: (row) => row.raw?.source === 'site' && row.raw?.ended === false,
  }),
  dealTeam: (tile, ctx) => ({
    onRowEdit: (row) => ctx.editTeamMember(row),
    rowEditLabel: t('deal.team.edit'),
    rowEditWhen: live,
    onRowAction: (id) => { const row = tile.rows?.find((r) => r.id === id); if (row) ctx.endRow('dealTeam', row); },
    rowActionLabel: t('record.tile.end'),
    rowActionWhen: live,
  }),
  dealContacts: (tile, ctx) => ({ onRowAction: (id) => { const row = tile.rows?.find((r) => r.id === id); if (row) ctx.endRow('dealContacts', row); }, rowActionLabel: t('record.tile.end'), rowActionWhen: live }),
  // A site's current role ends from its row and is never edited in place; a changed role is a new row after it (decision 0077).
  roles: (tile, ctx) => ({ onRowAction: (id) => { const row = tile.rows?.find((r) => r.id === id); if (row) ctx.endRow('roles', row); }, rowActionLabel: t('record.tile.end'), rowActionWhen: (row) => row.pill === 'Current' && row.raw?.ended !== true }),
  // The Sales Person row is the assignment, changed through the record's own edit, never ended here.
  accountRoles: (tile, ctx) => ({ onRowAction: (id) => { const row = tile.rows?.find((r) => r.id === id); if (row) ctx.endRow('accountRoles', row); }, rowActionLabel: t('record.tile.end'), rowActionWhen: (row) => row.pill === 'Current' && typeof row.raw?.roleTypeId === 'string' }),
  // 🔨 A contact row is corrected and removed from one modal, and the row's own
  // × is gone (decision 0589): a removal one press from the row is a deletion nobody
  // confirmed (product owner, 2026-09-20), so Remove stands inside the correction it
  // belongs beside.
  contactMethods: (_tile, ctx) => ({
    onRowEdit: (row) => ctx.editContactMethod(row),
    rowEditLabel: t('contactModal.edit'),
    rowEditWhen: live,
  }),
  // 🔒 Decision 2972: a mailbox is corrected and ended from one window, as a contact line is (decision 0589).
  mailboxes: (_tile, ctx) => (ctx.editMailbox ? { onRowEdit: (row) => ctx.editMailbox?.(row), rowEditLabel: t('mailbox.edit') } : {}),
  // 🔒 Decision 7501: an Owned By line's pencil is End Ownership, and + Add Owner stands on the last one, each where the server says this viewer writes the ownership.
  generalInformation: (_tile, ctx) => ({
    ...(ctx.endOwnership ? { onRowEdit: (row: TileRow) => ctx.endOwnership?.(row), rowEditLabel: t('ownership.end'), rowEditWhen: ownershipEndable } : {}),
    ...(ctx.addOwner ? { onRowAction: () => ctx.addOwner?.(), rowActionLabel: t('ownership.add'), rowActionWhen: (row: TileRow) => row.raw?.addsOwner === true } : {}),
  }),
  // 🔒 Decision 7501: an open Subsidiaries row ends from its pencil; a Past row is history and carries none.
  subsidiaries: (_tile, ctx) => (ctx.endOwnership ? { onRowEdit: (row) => ctx.endOwnership?.(row), rowEditLabel: t('ownership.end'), rowEditWhen: ownershipEndable } : {}),
};

/** The tile keys the registry knows, for the test that holds every endable tile to an entry. */
export const ROW_ACTION_TILES: readonly string[] = Object.keys(REGISTRY);

/**
 * 🔒 Delete this link stands on every tile's rows, never through a tile's own
 * entry (T728; decision 1500): the server marks which rows are links this
 * viewer may delete, so an employment, a contact, a site role, a custody and a
 * company's owner (its Owned By line, decision 7501) all take it from one place.
 */
function deleteLinkActions(ctx: RowActionContext): RowActions {
  const open = ctx.deleteLink;
  if (!open) return {};
  return {
    onRowDelete: (row) => { const link = linkTargetOf(row.raw); if (link) open(link); },
    rowDeleteWhen: (row) => linkTargetOf(row.raw) !== null,
  };
}

export function rowActionsFor(tile: TileAnswer, ctx: RowActionContext): RowActions {
  const entry = REGISTRY[tile.key];
  return { ...(entry ? entry(tile, ctx) : {}), ...deleteLinkActions(ctx) };
}

/**
 * 🔒 Move and Remove on a certificate card (decision 7891; T3181): offered only where no document names the certificate,
 * Move on a live one alone, each opening its dialog. A page that passes neither callback draws neither.
 */
export function certificateMenuActs(row: TileRow, ctx: Pick<RowActionContext, 'removeCertificate' | 'moveCertificate'>): { key: string; label: string; onAct: () => void; separated?: boolean }[] {
  if (!row.id || row.raw?.named !== false) return [];
  return [
    ...(ctx.moveCertificate && live(row) ? [{ key: 'move', label: t('certificate.move'), onAct: () => ctx.moveCertificate?.(row) }] : []),
    ...(ctx.removeCertificate ? [{ key: 'remove', label: t('certificate.remove'), onAct: () => ctx.removeCertificate?.(row), separated: true }] : []),
  ];
}
